import { showEntri } from "entrijs";
import { isRootDomain } from "replo-utils/lib/url";

// Cloudflare static IP addresses for root domain DNS A records
// These are duplicated from the backend to avoid cross-boundary imports
const CLOUDFLARE_STATIC_IPV4 = ["162.159.142.94", "172.66.2.90"];

// Cloudflare static IPv6 addresses for root domain DNS AAAA records
// These might not need to be used but just in case (and we were given these)
const CLOUDFLARE_STATIC_IPV6 = ["2606:4700:7::252", "2a06:98c1:58::252"];

type DNSRecordType =
  | "A"
  | "AAAA"
  | "CNAME"
  | "MX"
  | "TXT"
  | "NS"
  | "PTR"
  | "SRV"
  | "CAA";

interface DNSRecord {
  host: string;
  ttl: number;
  type: DNSRecordType;
  value: string;
  fallbackValue?: string;
  priority?: number; // Only for MX records
}

interface EntriConfig {
  applicationId: string;
  dnsRecords: DNSRecord[];
  token: string;
  applicationName?: string;
  defaultSubdomain?: string;
  forceSubdomain?: boolean;
  hostRequired?: boolean;
  manualSetupDocumentation?: string;
  supportForSubdomains?: boolean;
  userId?: string;
  wwwRedirect?: boolean;
  prefilledDomain?: string;
}

/**
 * Constructs DNS records based on whether the domain is a root (apex) domain or subdomain.
 * Root domains use A records pointing to Cloudflare proxy IPs. IPv6 IPs are also included but likely not used.
 * Subdomains use CNAME records for backward compatibility.
 *
 * @param shopDomainPrefix - The shop's domain prefix (e.g., "myshop")
 * @param isRootDomain - Whether the domain is a root/apex domain (split(".").length === 1)
 * @returns Array of DNS records to be configured in the DNS provider
 */
const constructDNSRecords = (
  shopDomainPrefix: string,
  isRootDomain: boolean,
): DNSRecord[] => {
  if (isRootDomain) {
    // For apex domains, return A records pointing to Cloudflare apex proxy IPs. IPv6 IPs are also included but likely not used.
    // Add IPv4 A records (@  represents the root/apex domain)
    const ipv4Records = CLOUDFLARE_STATIC_IPV4.map((ip) => ({
      host: "@" as const,
      type: "A" as const,
      value: ip,
      ttl: 300,
    }));

    // Add IPv6 records
    const ipv6Records = CLOUDFLARE_STATIC_IPV6.map((ip) => ({
      host: "@" as const,
      type: "AAAA" as const,
      value: ip,
      ttl: 300,
    }));

    return [...ipv4Records, ...ipv6Records];
  }

  return [
    {
      host: "{SUBDOMAIN}",
      type: "CNAME",
      value: `${shopDomainPrefix}.reploshops.com`,
      ttl: 300,
    },
  ];
};

/**
 * Opens the Entri modal for the user to configure DNS records for their custom domain.
 * The modal will guide the user through setting up either A records (for root domains)
 * or CNAME records (for subdomains) in their DNS provider.
 *
 * @param applicationId - The Entri application ID
 * @param token - The Entri token
 * @param shopDomainPrefix - The shop's domain prefix used for CNAME value
 * @param customDomain - The custom domain the user wants to link to their shop
 */
const showEntriModal = (
  applicationId: string,
  token: string,
  shopDomainPrefix: string,
  customDomain: string,
) => {
  const isRoot = isRootDomain(customDomain);
  const dnsRecords = constructDNSRecords(shopDomainPrefix, isRoot);

  const config: EntriConfig = {
    applicationId,
    dnsRecords,
    token,
    applicationName: "Replo",
    prefilledDomain: customDomain,
    wwwRedirect: !isRoot, // No www redirect for apex domains
    forceSubdomain: !isRoot, // Only force subdomain for non-apex domains
  };

  void showEntri(config);
};

export default showEntriModal;
